import React, {
 useContext, useState, useEffect
} from 'react';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from 'shared/components/ui/dialog';
import {
 Input
} from 'shared/components/ui/input';
import {
 Label
} from 'shared/components/ui/label';
import {
 Button
} from 'shared/components/ui/button';
import {
 applicationService
} from 'shared/api/services/BEXO';
import {
 Context
} from 'shared/context/GlobalState';
import {
 useNavigate
} from 'react-router-dom';

// Define types for the props and application fields
interface Settings {
    default_page: string;
    app_logo: string;
}

interface NewApp {
    tenant_id: string;
    full_name: string;
    name: string;
    settings: Settings;
    active: boolean;
}

interface CreateAppProps {
    isOpen: boolean;
    setOpenApplicationModal: React.Dispatch<React.SetStateAction<boolean>>;
    oragID: string;
    fetchApplication: () => Promise<void>;
}

// AppBuilderHandler for validation
class AppBuilderHandler {
    validateAppName(value: string) {
        if (/\s/.test(value)) {
            return {
                isValid: false,
                message: 'App Name cannot contain spaces.'
            };
        }
        if (!/^[a-zA-Z0-9_]+$/.test(value)) {
            return {
                isValid: false,
                message: 'App Name can only contain letters, numbers, and underscores.',
            };
        }
        if (value.length < 4) {
            return {
                isValid: false,
                message: 'App Name should be at least 4 characters long.'
            };
        }
        return {
            isValid: true,
            message: ''
        };
    }
}

const CreateApp: React.FC<CreateAppProps> = ({
    isOpen, setOpenApplicationModal, oragID, fetchApplication
}) => {
    const validator = new AppBuilderHandler();
    const [newAppObj, setNewAppObj] = useState<NewApp>({
        tenant_id: oragID,
        full_name: '',
        name: '',
        settings: {
            default_page: '/login',
            app_logo: '',
        },
        active: true,
    });

    const {
 addNewNotifcation
} = useContext(Context);
    const navigate = useNavigate();
    const [loading, setLoading] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string>('');

    // Handle Input Change
    const handleChange = (value: string, field: keyof NewApp) => {
        if (field === 'full_name') {
            // Replace spaces with underscores before setting state
            const updatedValue = value.replace(/\s+/g, '_');
            setNewAppObj(prev => ({
                ...prev,
                [field]: updatedValue
            }));
        }
    };

    // Validate the app name and update the validation error message
    useEffect(() => {
        const {
 isValid, message
} = validator.validateAppName(newAppObj.full_name);
        if (!isValid) {
            setValidationError(message);
        } else {
            setValidationError(''); // Clear the error if valid
        }
    }, [newAppObj.full_name]); // Only run this effect when `newAppObj.full_name` changes

    // Handle Form Submission
    const handleOnSubmit = async () => {
        const {
 isValid
} = validator.validateAppName(newAppObj.full_name);

        if (!isValid) {
            return; // Don't submit if the input is invalid
        }

        try {
            setLoading(true);
            newAppObj.name = newAppObj.full_name;

            const response = await applicationService.createApplication(newAppObj);
            addNewNotifcation('App Created Successfully', 'success');
            setOpenApplicationModal(false);

            // Navigate to the new app's Data Builder
            const appId = response.data?.app_id;
            if (appId) {
                navigate(`${appId}/data-builder`);
            } else {
                await fetchApplication();
            }
        } catch (error) {
            console.error('Error:', error);

            if (error.response) {
                if (error.response.status === 400) {
                    addNewNotifcation(`Application "${newAppObj.full_name}" already exists`, 'danger');
                } else {
                    addNewNotifcation('Error creating application', 'danger');
                }
            } else {
                addNewNotifcation('Unexpected error occurred', 'danger');
            }
        } finally {
            setLoading(false);
        }
    };

    // Validate Input and Update Button Disabled State
    const isSubmitDisabled = !validator.validateAppName(newAppObj.full_name).isValid || loading;

    return (
        <Dialog open={isOpen} onOpenChange={() => setOpenApplicationModal(false)}>
            <DialogContent className="sm:max-w-[600px]">
                <DialogHeader>
                    <DialogTitle>Create New App</DialogTitle>
                    <DialogDescription>Create a new application with a globally unique name.</DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="flex flex-col gap-4">
                        <Label htmlFor="app-name">App Name</Label>
                        <Input
                            id="app-name"
                            value={newAppObj.full_name}
                            onChange={(e) => handleChange(e.target.value, 'full_name')}
                            placeholder="Enter App Name"
                        />
                        {validationError && <p className="text-red-500 text-sm">{validationError}</p>}
                    </div>
                    <div className="hidden">
                        <Label htmlFor="settings">Default Page</Label>
                        <Input
                            id="settings"
                            value={newAppObj.settings.default_page}
                            readOnly
                        />
                    </div>
                </div>
                <DialogFooter className="mt-3">
                    <Button onClick={() => setOpenApplicationModal(false)} variant="outline">Cancel</Button>
                    <Button onClick={handleOnSubmit} disabled={isSubmitDisabled}>
                        {loading ? 'Creating...' : 'Create App'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default CreateApp;
